import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import {
  Crown, Users, Radio, Plus, DollarSign, ArrowRight, Trophy,
  FileSignature, Copy, Percent, ShieldCheck,
} from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";

const Stat = ({ label, value, icon: Icon, color = "#fff", testId }) => (
  <div className="border border-white/10 bg-[#141414] p-4" data-testid={testId}>
    <div className="text-white/40 text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5">
      <Icon size={11} /> {label}
    </div>
    <div className="font-display text-3xl mt-1" style={{ color }}>{value}</div>
  </div>
);

export default function Manager() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  usePageMeta({ title: "Manager Hub · PickemSports.club", path: "/manager", noindex: true });

  const load = () => api.get("/friends/manager/summary").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const optOut = async () => {
    setBusy(true);
    try {
      await api.post("/friends/manager/opt-in", { opt_in: false });
      toast.success("Opted out — you keep anything already accrued");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally { setBusy(false); }
  };

  const copyLink = () => {
    const link = `${window.location.origin}/friends?m=${data.share_link_code}`;
    navigator.clipboard?.writeText(link);
    toast.success("Manager link copied — subscribers from this link are credited to you");
  };

  if (!data) {
    return (
      <Layout>
        <div className="text-white/40 py-20 text-center font-display text-2xl tracking-widest animate-pulse">
          LOADING...
        </div>
      </Layout>
    );
  }

  const t = data.totals;
  const rs = data.revenue_share;
  const capPct = Math.min(100, (rs.games_this_period / Math.max(1, rs.game_cap)) * 100);

  return (
    <Layout>
      <div className="mb-8">
        <div className="text-[#FFD700] text-xs uppercase tracking-widest mb-2 inline-flex items-center gap-1.5">
          <Crown size={13} /> Manager hub
        </div>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tight">YOUR GAMES</h1>
        <p className="text-white/50 text-sm mt-2">
          Monitor every challenge you run — who joined, who's entered, and what your revenue share
          looks like this month.
        </p>
      </div>

      <div className="grid sm:grid-cols-4 gap-3 mb-6">
        <Stat label="Challenges" value={t.challenges} icon={Trophy} testId="manager-stat-challenges" />
        <Stat label="Live now" value={t.live} icon={Radio} color="#00FF66" testId="manager-stat-live" />
        <Stat label="Active subs" value={rs.subscribers.active} icon={Users} testId="manager-stat-subs" />
        <Stat label="Earned (unpaid)" value={`$${t.accrued.toFixed(2)}`} icon={DollarSign}
          color="#FFD700" testId="manager-stat-earned" />
      </div>

      {!data.agreement.signed ? (
        <div className="border border-[#FFD700]/30 bg-[#FFD700]/[0.05] p-5 mb-6" data-testid="manager-contract-gate">
          <div className="text-[#FFD700] text-xs uppercase tracking-widest mb-1">Get paid to run games</div>
          <h2 className="font-display text-2xl mb-2">SIGN THE GAME MANAGER AGREEMENT</h2>
          <p className="text-white/60 text-sm max-w-2xl">
            Game Managers are independent contractors. You earn{" "}
            <strong className="text-white">{rs.share_pct.toFixed(0)}%</strong> of the subscription
            revenue from members you bring in — after processing fees — and you may run up to{" "}
            <strong className="text-white">{rs.game_cap} games a month</strong>. Nothing is charged to
            play and no prizes are ever paid out.
          </p>
          <Link to="/manager/agreement" data-testid="manager-sign-agreement-btn"
            className="mt-4 inline-flex items-center gap-2 bg-[#FFD700] hover:bg-[#e6c200] text-black font-bold uppercase tracking-widest px-4 py-2.5 text-xs rounded-sm">
            <FileSignature size={14} /> Read and sign
          </Link>
        </div>
      ) : (
        <div className="border border-[#FFD700]/30 bg-[#FFD700]/[0.05] p-5 mb-6" data-testid="manager-programme">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="max-w-2xl">
              <div className="text-[#FFD700] text-xs uppercase tracking-widest mb-1 inline-flex items-center gap-1.5">
                <ShieldCheck size={12} /> Contract signed {new Date(data.agreement.signed_at).toLocaleDateString()}
                {" "}· v{data.agreement.version}
              </div>
              <h2 className="font-display text-2xl mb-2">REVENUE SHARE</h2>
              <p className="text-white/60 text-sm">
                You earn <strong className="text-white">{rs.share_pct.toFixed(0)}%</strong> of every
                subscription you bring in, after processing fees ({rs.fee_pct}% + ${rs.fee_flat.toFixed(2)}),
                on the first charge and every renewal. Settled monthly to{" "}
                <strong className="text-white">{data.payout_email}</strong> once you clear ${Number(data.payout_threshold).toFixed(0)}.
              </p>
              <div className="flex gap-2 mt-3 flex-wrap">
                <button onClick={copyLink} data-testid="manager-copy-link-btn"
                  className="border border-white/20 hover:border-[#FFD700] text-white text-[10px] uppercase tracking-widest font-bold px-3 py-2 rounded-sm inline-flex items-center gap-1.5">
                  <Copy size={11} /> Copy manager link
                </button>
                <Link to="/manager/agreement" data-testid="manager-view-agreement-link"
                  className="border border-white/20 hover:border-white/40 text-white/70 text-[10px] uppercase tracking-widest font-bold px-3 py-2 rounded-sm inline-flex items-center gap-1.5">
                  <FileSignature size={11} /> View agreement
                </Link>
                {data.is_manager && (
                  <button onClick={optOut} disabled={busy} data-testid="manager-optout-btn"
                    className="border border-white/10 hover:border-white/30 text-white/40 text-[10px] uppercase tracking-widest font-bold px-3 py-2 rounded-sm">
                    Opt out
                  </button>
                )}
              </div>
            </div>
            <div className="min-w-[220px]">
              <div className="text-white/40 text-[10px] uppercase tracking-widest">
                Games this month · {rs.period}
              </div>
              <div className="font-display text-4xl">
                {rs.games_this_period}<span className="text-white/30 text-2xl">/{rs.game_cap}</span>
              </div>
              <div className="h-1.5 bg-white/10 rounded-full mt-2 overflow-hidden">
                <div className="h-full bg-[#FFD700] transition-all" style={{ width: `${capPct}%` }} />
              </div>
              <div className="mt-3 text-white/40 text-[10px] uppercase tracking-widest">
                Net revenue tracked
              </div>
              <div className="font-display text-2xl text-[#00FF66] inline-flex items-center gap-1">
                ${rs.revenue.net.toFixed(2)}
                <Percent size={13} className="text-white/30" />
              </div>
              <div className="text-white/30 text-[10px]">
                ${rs.revenue.gross.toFixed(2)} gross · ${rs.revenue.fees.toFixed(2)} fees
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="font-display text-2xl">MY CHALLENGES</div>
        <Link
          to="/friends/new"
          data-testid="manager-new-challenge-btn"
          className="bg-[#00FF66] hover:bg-[#00e65b] text-black font-bold uppercase tracking-widest px-4 py-2.5 text-xs rounded-sm inline-flex items-center gap-2"
        >
          <Plus size={14} /> New challenge
        </Link>
      </div>

      {data.challenges.length === 0 ? (
        <div className="border border-white/10 bg-[#141414] p-10 text-center text-white/50 text-sm"
          data-testid="manager-empty">
          You haven't created a challenge yet.
        </div>
      ) : (
        <div className="space-y-2" data-testid="manager-challenges">
          {data.challenges.map((c) => (
            <Link
              key={c.contest_id}
              to={`/contest/${c.contest_id}`}
              data-testid={`manager-challenge-${c.contest_id}`}
              className="block border border-white/10 bg-[#141414] hover:border-[#FFD700]/40 p-4"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{c.title}</div>
                  <div className="text-white/40 text-[10px] uppercase tracking-widest">
                    {c.sport} {c.position} · code {c.invite_code} · {c.status}
                  </div>
                </div>
                <div className="flex items-center gap-5 text-right">
                  <div>
                    <div className="text-white/40 text-[10px] uppercase tracking-widest">Invited</div>
                    <div className="font-display text-lg">{c.members}</div>
                  </div>
                  <div>
                    <div className="text-white/40 text-[10px] uppercase tracking-widest">Entered</div>
                    <div className="font-display text-lg">{c.field}/{c.max_entries}</div>
                  </div>
                  <div>
                    <div className="text-white/40 text-[10px] uppercase tracking-widest">Chat</div>
                    <div className="font-display text-lg">{c.messages}</div>
                  </div>
                  <ArrowRight size={16} className="text-white/30" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {data.earnings?.length > 0 && (
        <div className="mt-6 border border-white/10 bg-[#141414]" data-testid="manager-earnings">
          <div className="p-4 border-b border-white/10 font-display text-xl">REVENUE SHARE LEDGER</div>
          <div className="divide-y divide-white/10">
            {data.earnings.map((e) => (
              <div key={e.earning_id} className="flex items-center justify-between p-3 text-sm gap-3">
                <div className="min-w-0">
                  <div className="font-semibold truncate">
                    {e.subscriber_name || "Subscriber"} · {e.plan_id || "plan"}
                  </div>
                  <div className="text-white/40 text-[10px] uppercase tracking-widest">
                    {e.charge_kind} · ${Number(e.gross || 0).toFixed(2)} gross − ${Number(e.fee || 0).toFixed(2)} fees
                    {" "}· {new Date(e.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-display text-lg text-[#FFD700]">${e.amount.toFixed(2)}</div>
                  <div className="text-white/40 text-[10px] uppercase tracking-widest">{e.status}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Layout>
  );
}
